/**
 * CALLCENTER-QUEUE-QUALIFY-V1 (V2-P1) -- the call-center operator COMMAND
 * CENTER. The founder's 30-second glance: a SUMMARY STRIP first (open /
 * unassigned / overdue / booked-today -- computed counts, each a tappable
 * filter), ONE list beneath (source, stage chip, owner chip or POOL badge,
 * age, next-action, per row), and ONE primary action per row state (Claim when
 * the pool + mode permit; Qualify when a contact attempt is logged; else Work,
 * which drills into the canonical Lead-360). A Q2 pre-visit section follows.
 *
 * EVOLVES the existing lead-cockpit IN PLACE (03 open question 1): mounted by
 * the same /dashboard/lead-cockpit route only when the /me `intakeQueueEnabled`
 * signal is present; the legacy cockpit renders byte-identically otherwise.
 * NOT a parallel lead list -- the canonical Lead-360 stays the only detail
 * view (every row drills there).
 *
 * SINGLE COMPUTATION: the strip numbers come from the server's ONE
 * queue-health module (intake.health), and the list is the SAME
 * intake.rows array filtered client-side by `filterIntakeRows` -- the exact
 * mirror of the backend partition. So the strip can never disagree with the
 * list (a render test pins strip == filtered length).
 *
 * FOUR PLANES, never mixed: stage chip / owner chip (V2-P0 projection) / queue
 * (the list membership) / next-action label. Recording goes ONLY through
 * EXISTING write paths -- claim via the V2-P0 route, qualify via the EXISTING
 * lead-stage engine (advanceLeadStage -> POST /api/leads/:id/stage). No new
 * stage logic, no new status.
 *
 * PHI-free: renders ids / carved administrative name / source + stage tokens /
 * ages / the owner label + counts only. Contact channel stays behind the
 * audited reveal on the Lead-360; nothing clinical appears here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDashboardMeContext } from '@/dashboard/me-context';
import type { DashboardMe } from '@/dashboard/types';
import {
  advanceLeadStage,
  claimLead,
  fetchCallcenterQueue,
  recordPrevisitDisposition,
  type CallcenterQueueRowView,
  type CallcenterQueueView,
  type ChaseDisciplineView,
  type ChaseQueueRowView,
  type PreVisitQueueRowView,
  type PrevisitDispositionReasonView,
  emptyInboundCallActivity,
  type InboundCallActivityRowView,
  type InboundCallActivityView,
  type InboundCallIdentityStateView,
} from '@/dashboard/api';
import type {
  Lead360SlaBreach,
  Lead360SlaState,
  LeadOwnershipView,
} from '@/dashboard/owner/lead-360-view';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import {
  resolveClaimAffordance,
  type ClaimAffordance,
} from '@/dashboard/leads/LeadOwnerControls';
// NEVER-BOUNCE RULE (item 1): the read-only copy is the SAME single source the
// LeadQuickView footer already uses for a caller with no live action -- one
// sentence for "your role reads this, it does not open it", never a second.
import { QUICKVIEW_READONLY_COPY } from '@/dashboard/leads/LeadQuickView';
import { AcBadge } from '@/dashboard/ui';
import {
  classMeta,
  classShortLabel,
  formatLeakageAge,
  isKnownBreachClass,
  SLA_CLASS_ORDER,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import { PortalApiError } from '@/lib/portal-api';

// --- Copy (single source; asserted in tests) --------------------------------

export const CC_EMPTY_INTAKE =
  'No new enquiries waiting. New leads land here the moment they arrive, from every source.';
export const CC_EMPTY_PREVISIT = 'Every booked patient is confirmed. Nothing to chase.';
export const CC_ERROR_FALLBACK = 'Could not load the intake queue. Retry.';
export const CC_CHASE_TITLE = 'Chase queue - leads stalling before the visit';
export const CC_EMPTY_CHASE =
  'No leads are stalling before the visit. Quiet enquiries and unconfirmed bookings surface here to chase.';
export const CC_WHATSAPP_UNAVAILABLE =
  'WhatsApp follow-up not available (send path not implemented)';
export const CC_CHASE_PROVISIONAL =
  'Disposition reasons are provisional (pending tenant confirmation).';

// --- Phase 3: inbound call activity (OMNICHANNEL-CONTACT-CENTER-OS-V1) ------
// Copy lives here as a single source, asserted in tests -- never inline.

export const CC_CALLS_TITLE = 'Calls that came in';
export const CC_CALLS_HINT =
  'Every call the clinic received, and who it was from. A caller we do not recognise is a new enquiry, not an error.';
/** The honest seam. AdoraCare has NO outbound calling carrier: Phase 1 proved
 *  the Office24x7 writeback verbs throw and no dialler exists. So this surface
 *  offers no call button -- it says so, rather than rendering one that cannot
 *  dial. */
export const CC_CALLS_NO_OUTBOUND_LINE =
  'Calling back happens on your phone -- AdoraCare has no outbound calling line.';

/** COC-STATION-INBOUND-CALLS-V1 -- the MONITOR variant's two lines. The COC
 *  is the SUPERVISOR of inbound calling, never its owner (the call centre
 *  takes the call; the visited clinic's front desk owns the follow-up for
 *  the org's configured window). So the monitor copy states whose work this
 *  is, and the panel offers the COC no action on it -- the same
 *  "visible, not owned" posture the followup_ownership config already gives
 *  the COC on an in-window case. */
export const CC_CALLS_MONITOR_HINT =
  'What is reaching the clinics you supervise. The call centre actions these, not you -- they are here so you can see it happening.';
export const CC_CALLS_MONITOR_LINE =
  'Watching only. If something here needs chasing, tell the team that owns it.';

/** Human labels for the two chase classes (the SLA classifier's two classes;
 *  never re-typed elsewhere). */
export const CHASE_CLASS_LABEL: Readonly<
  Record<'enquired_never_booked' | 'booked_never_visited', string>
> = {
  enquired_never_booked: 'Enquired - never booked',
  booked_never_visited: 'Booked - never visited',
};
export const CC_POOL_BADGE = 'POOL';
export const CC_ESCALATED_BADGE = 'Escalated';
export const CC_CLAIM = 'Claim';
export const CC_WORK = 'Work >';
export const CC_QUALIFY = 'Qualify';
export const CC_QUALIFY_CONFIRM = 'Confirm qualify';
export const CC_QUALIFY_CANCEL = 'Cancel';
export const CC_QUALIFY_HINT =
  'Confirm every item, then qualify. Qualifying advances the lead through the existing stage engine.';

// --- Filters (mirror lib/queue-health partitionIntakeQueue) -----------------

export type IntakeFilter = 'all' | 'mine' | 'unassigned' | 'overdue';

export const INTAKE_FILTERS: readonly IntakeFilter[] = [
  'all',
  'mine',
  'unassigned',
  'overdue',
];

export function filterIntakeRows(
  rows: readonly CallcenterQueueRowView[],
  filter: IntakeFilter,
): CallcenterQueueRowView[] {
  switch (filter) {
    case 'all':
      return [...rows];
    case 'mine':
      return rows.filter((r) => r.ownerAssignment?.isMine === true);
    case 'unassigned':
      return rows.filter(
        (r) => r.ownerAssignment !== null && r.ownerAssignment.assigned === false,
      );
    case 'overdue':
      return rows.filter((r) => r.ownerAssignment?.escalated === true);
    default:
      return [...rows];
  }
}

/** The count each filter chip shows -- the SAME partition the list renders. */
export function filterCount(
  rows: readonly CallcenterQueueRowView[],
  filter: IntakeFilter,
): number {
  return filterIntakeRows(rows, filter).length;
}

/**
 * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315, Part D): the OPT-IN
 * "SLA breaches first" sort (user-visible label; the identifier stays
 * sortOverdueFirst / overdueFirst to avoid churn) -- a STABLE reorder that
 * floats SLA-breached rows to the
 * top (worst severity first, then the oldest breach age), preserving the
 * server's DEFAULT order among equals and for every non-breached row (the
 * original index is the final tiebreak). OPT-IN: the default view never calls
 * this, so the default ordering stays byte-identical (test-pinned). Read-only;
 * derives entirely from the row's existing slaState (no new wire, no re-fetch).
 */
export function sortOverdueFirst(
  rows: readonly CallcenterQueueRowView[],
): CallcenterQueueRowView[] {
  const rank = (r: CallcenterQueueRowView): number => {
    const breaches = r.slaState?.breaches ?? [];
    if (breaches.length === 0) return 0; // not breached -> stays below
    return breaches.some((b) => b.severity === 'red') ? 2 : 1; // red outranks amber
  };
  const oldestBreachAge = (r: CallcenterQueueRowView): number =>
    (r.slaState?.breaches ?? []).reduce(
      (max, b) => (b.ageMinutes > max ? b.ageMinutes : max),
      0,
    );
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const ra = rank(a.r);
      const rb = rank(b.r);
      if (ra !== rb) return rb - ra; // higher severity rank first
      if (ra !== 0) {
        const diff = oldestBreachAge(b.r) - oldestBreachAge(a.r);
        if (diff !== 0) return diff; // oldest breach first
      }
      return a.i - b.i; // STABLE: preserve the server default order otherwise
    })
    .map((x) => x.r);
}

// --- Pure display helpers ---------------------------------------------------

export function formatAge(minutes: number | null): string {
  if (minutes === null) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Humanize a stage token for the chip (never re-typing the domain vocabulary). */
export function stageLabel(stage: string | null): string {
  if (stage === null || stage.trim().length === 0) return 'New lead';
  return stage
    .split('_')
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

/** Owner initials for the compact chip (mirrors the owner LeadsListPage). */
export function ownerInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** The lead's drill target -- the ONE canonical Lead-360 detail view. */
export function drillHref(safeLeadId: string): string {
  return `/dashboard/leads/${encodeURIComponent(safeLeadId)}`;
}

/**
 * DEEPAK-FRONT-OFFICE-FEEDBACK-CORRECTIVE-V1 (item 1; tenant finding
 * 2026-07-27) -- THE NEVER-BOUNCE RULE.
 *
 * THE BUG: this command center rendered every lead row's name (and the Work
 * button) as an UNCONDITIONAL link to /dashboard/leads/:safeLeadId. That route
 * is guarded by RequireLeadsWorkspace, whose decision keys off
 * `me.leadsWorkspaceEnabled` -- a signal the backend emits only for
 * LEADS_WORKSPACE_ROLES (api/routes/leads-workspace.ts). gm_agm and
 * regional_head ARE admitted to this surface (LEAD_COCKPIT_ROLES +
 * CALLCENTER_QUEUE_AUDIENCE_ROLES) but were NOT in that set, so a manager
 * clicking a lead was silently redirected to `me.homePath` = /dashboard/agm --
 * the "Zone overview" AGM home. A door that silently teleports the user home is
 * the worst of both worlds: it neither opens nor explains.
 *
 * THE RULE (doctrine 06 + LES-016 no dead doors): a lead row NEVER offers a
 * door it cannot open, and NEVER redirects to a role home. When the caller
 * holds the canonical-page signal the drill is unchanged (byte-identical for
 * receptionist / coc / owner / call_center_agent); when the caller does not,
 * the row renders as a NON-NAVIGATING name plus the honest read-only note --
 * the SAME posture LeadQuickView already takes with its "Open full view" link
 * (frontend/src/dashboard/leads/LeadQuickView.tsx) and the manager tracking
 * strip already takes with its drill rows.
 *
 * THE ROUTE WAS SINCE WIDENED (MANAGER-LEAD-READ-ACCESS-CORRECTIVE-V1, founder
 * ruling 2026-07-28 -- #387's registered follow-up P-1, now closed). gm_agm and
 * regional_head are in LEADS_WORKSPACE_ROLES, so the backend emits
 * `me.leadsWorkspaceEnabled` for them and `canReachLeadDetail` below now
 * returns TRUE: the manager's click OPENS the canonical Lead 360 instead of
 * rendering the read-only note. NOTHING IN THIS FILE CHANGED TO ACHIEVE THAT --
 * the rule below is stated in terms of the signal, never a role literal, so
 * widening the audience upstream was the whole fix. That is the point of the
 * rule: reachability has exactly ONE home.
 *
 * THE RULE STILL BINDS, unchanged, for every caller the canonical page does not
 * admit (doctor, patient, and any future role outside the audience): they are
 * offered NO door and told so honestly. And SEE-NOT-DO still holds for the two
 * newly-admitted managers -- opening the record grants them no action on it
 * (the write gate is LEADS_WORKSPACE_ACTION_ROLES, which they are NOT in).
 */
export function canReachLeadDetail(me: DashboardMe | null): boolean {
  return me?.leadsWorkspaceEnabled === true;
}

/**
 * The lead's drill href, or NULL when the caller cannot open the canonical
 * page. Pure; the single decision point every row in this file consults.
 */
export function drillHrefFor(
  safeLeadId: string,
  leadDetailReachable: boolean,
): string | null {
  return leadDetailReachable ? drillHref(safeLeadId) : null;
}

/**
 * The row NAME cell content: a drill link when the page is reachable, plain
 * non-navigating text when it is not. One helper so every row type (intake /
 * pre-visit / chase) states the rule identically.
 */
function LeadNameCell({
  href,
  label,
  testId,
}: {
  href: string | null;
  label: string;
  testId: string;
}): JSX.Element {
  const text = label.trim().length > 0 ? label : '(no name)';
  return href !== null ? (
    <a href={href} data-testid={testId}>
      {text}
    </a>
  ) : (
    <span data-testid={`${testId}-static`}>{text}</span>
  );
}

// --- Pure sub-views ---------------------------------------------------------

export function OwnerCell({
  ownership,
}: {
  ownership: LeadOwnershipView | null;
}): JSX.Element {
  if (ownership === null) {
    // V2-P0 flag off -> no ownership data; render a neutral dash, never a crash.
    return <span className="cc-owner cc-owner--none">{'-'}</span>;
  }
  if (ownership.assigned) {
    /* Identity never borrows status colors (02 sec 9.5; 05 A4). */
    return (
      <span
        className="ac-badge ac-badge--brand"
        data-testid="cc-owner-chip"
        title={ownership.ownerLabel ?? undefined}
      >
        {ownerInitials(ownership.ownerLabel ?? '')}
      </span>
    );
  }
  return (
    <>
      <span className="ac-badge ac-badge--pool" data-testid="cc-owner-pool">
        {CC_POOL_BADGE}
      </span>
      {ownership.escalated ? (
        <span className="ac-badge ac-badge--pending" data-testid="cc-owner-escalated">
          {CC_ESCALATED_BADGE}
        </span>
      ) : null}
    </>
  );
}

/**
 * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): the queue-header SLA
 * breach summary -- per-class breach counts across the SAME rows the list
 * renders (health.slaBreachCounts, the backend single-computation truth, so the
 * strip counts can never disagree with the row chips). ONE static count chip
 * per breached class, in canonical engine order; the whole strip is ABSENT when
 * the queue has zero breaches (a healthy queue raises no alarm). NARROW: class
 * token + count only. The tooltip carries the class action hint (plain-language
 * ONE-place-per-copy from sla-leakage-view.classMeta).
 */
export function SlaBreachStrip({
  counts,
}: {
  counts: Readonly<Record<string, number>> | null | undefined;
}): JSX.Element | null {
  // Tolerate an ABSENT counts map (older/partial wire) -- degrade to no strip,
  // exactly like all-zero (fail-soft; normalized at the API boundary, this guard
  // is defense-in-depth for the exported component).
  const breached = SLA_CLASS_ORDER.filter((cls) => (counts?.[cls] ?? 0) > 0);
  if (breached.length === 0) {
    return null;
  }
  return (
    <div className="cc-strip__sla" data-testid="cc-sla-strip">
      <span className="cc-strip__sla-label">SLA breaches</span>
      {breached.map((cls) => (
        <span
          key={cls}
          className="cc-chip cc-chip--sla"
          data-testid={`cc-sla-count-${cls}`}
          title={classMeta(cls).hint}
        >
          {classShortLabel(cls)} <b>{counts?.[cls] ?? 0}</b>
        </span>
      ))}
    </div>
  );
}

export function IntakeSummaryStrip({
  data,
  activeFilter,
  onFilter,
}: {
  data: CallcenterQueueView;
  activeFilter: IntakeFilter;
  onFilter?: (f: IntakeFilter) => void;
}): JSX.Element {
  const rows = data.intake.rows;
  const health = data.intake.health;
  const chip = (
    filter: IntakeFilter,
    label: string,
    count: number,
    testid: string,
  ): JSX.Element => (
    <button
      type="button"
      className={`cc-chip${activeFilter === filter ? ' cc-chip--active' : ''}`}
      aria-pressed={activeFilter === filter}
      data-testid={testid}
      onClick={onFilter ? () => onFilter(filter) : undefined}
    >
      {label} <b>{count}</b>
    </button>
  );
  return (
    <section className="cc-strip" data-testid="cc-summary-strip">
      <header className="cc-strip__head">
        <h2 className="cc-strip__desk" data-testid="cc-desk-label">
          {data.deskLabel}
        </h2>
        <span className="cc-strip__glance" data-testid="cc-glance-oldest">
          oldest {formatAge(health.oldestAgeMinutes)}
        </span>
      </header>
      <div className="cc-strip__chips">
        {chip('all', 'Open', health.open, 'cc-chip-open')}
        {health.ownershipAvailable
          ? chip('mine', 'Mine', filterCount(rows, 'mine'), 'cc-chip-mine')
          : null}
        {health.ownershipAvailable
          ? chip('unassigned', 'Unassigned (funnel)', health.unassigned, 'cc-chip-unassigned')
          : null}
        {health.ownershipAvailable
          ? chip('overdue', 'Overdue (funnel)', health.overdue, 'cc-chip-overdue')
          : null}
        <span className="cc-chip cc-chip--static" data-testid="cc-chip-booked-today">
          Booked today <b>{data.bookedToday}</b>
        </span>
        {data.pending > 0 ? (
          <a className="cc-chip cc-chip--link" href="/dashboard/org-intake" data-testid="cc-chip-pending">
            Pending <b>{data.pending}</b>
          </a>
        ) : null}
      </div>
      <SlaBreachStrip counts={health.slaBreachCounts} />
    </section>
  );
}

export interface RowActionHandlers {
  readonly claimAffordance: ClaimAffordance;
  readonly onClaim?: (leadId: number) => void;
  readonly onQualify?: (row: CallcenterQueueRowView) => void;
  readonly busyLeadId?: number | null;
}

/** The ONE primary action for a row's state (pool -> Claim; contacted ->
 *  Qualify; else Work -> drill). Always renders a primary action, even with no
 *  handlers (the pure Work drill), so every row is actionable. */
function PrimaryAction({
  row,
  actions,
  leadDetailReachable = true,
}: {
  row: CallcenterQueueRowView;
  actions?: RowActionHandlers;
  /** NEVER-BOUNCE RULE: default true keeps every existing caller/pin
   *  byte-identical; the live container ALWAYS passes the server-computed
   *  value, so production is never defaulted. */
  leadDetailReachable?: boolean;
}): JSX.Element {
  const drill = drillHrefFor(row.safeLeadId, leadDetailReachable);
  const isPool = row.ownerAssignment !== null && row.ownerAssignment.assigned === false;
  const busy = actions?.busyLeadId === row.leadId;
  if (isPool && actions?.claimAffordance === 'active' && actions.onClaim) {
    return (
      <button
        type="button"
        className="ac-btn ac-btn--primary ac-btn--sm"
        data-testid="cc-row-claim"
        disabled={busy}
        onClick={() => actions.onClaim?.(row.leadId)}
      >
        {CC_CLAIM}
      </button>
    );
  }
  if (row.stage === 'contacted' && actions?.onQualify) {
    return (
      <button
        type="button"
        className="ac-btn ac-btn--primary ac-btn--sm"
        data-testid="cc-row-qualify"
        disabled={busy}
        onClick={() => actions.onQualify?.(row)}
      >
        {CC_QUALIFY}
      </button>
    );
  }
  // NEVER-BOUNCE RULE: with no reachable canonical page there is no Work door
  // to offer, so the row states its honest read-only posture instead of a link
  // that would silently redirect the caller to their role home.
  if (drill === null) {
    return (
      <span className="cc-row__readonly" data-testid="cc-row-readonly">
        {QUICKVIEW_READONLY_COPY}
      </span>
    );
  }
  return (
    <a className="ac-btn ac-btn--ghost ac-btn--sm" data-testid="cc-row-work" href={drill}>
      {CC_WORK}
    </a>
  );
}

/**
 * RETURNING-PATIENT-RECOGNITION-AND-LEAD-LINKAGE-V1 (Refs #315): the compact
 * lead-time returning-patient CHIP (state only -- the fenced facts live in the
 * Lead 360 panel). CONFIDENT single match -> "Returning patient" (brand);
 * AMBIGUOUS shared-phone match -> "Possible match" (info, no name). Renders
 * NOTHING for a new / already-linked lead (tier null). FIVE-SECOND TEST: the
 * operator sees the returning signal on the row itself.
 */
export function ReturningChip({
  tier,
}: {
  tier: 'confident' | 'ambiguous' | null;
}): JSX.Element | null {
  if (tier === null) {
    return null;
  }
  if (tier === 'ambiguous') {
    return (
      <span
        className="ac-badge ac-badge--info cc-row__returning"
        data-testid="cc-row-returning"
        data-tier="ambiguous"
        title="More than one patient record shares this phone number"
      >
        Possible match
      </span>
    );
  }
  return (
    <span
      className="ac-badge ac-badge--brand cc-row__returning"
      data-testid="cc-row-returning"
      data-tier="confident"
      title="This phone matches an existing patient record"
    >
      Returning patient
    </span>
  );
}

/** Canonical engine order for a breach class; unknown tokens sort last. */
function slaClassRank(cls: string): number {
  const i = SLA_CLASS_ORDER.indexOf(cls as SlaBreachClass);
  return i === -1 ? SLA_CLASS_ORDER.length : i;
}

/** Breaches ordered by canonical engine class order (deterministic chip order).
 *  Tolerates an ABSENT (null OR undefined) or malformed state -- an older/partial
 *  wire shape degrades to no breaches, exactly like a healthy row (fail-soft;
 *  the wire is normalized at the API boundary, this guard is defense-in-depth
 *  for the exported helper). */
export function orderedBreaches(
  slaState: Lead360SlaState | null | undefined,
): readonly Lead360SlaBreach[] {
  if (slaState == null || !Array.isArray(slaState.breaches)) return [];
  return [...slaState.breaches].sort(
    (a, b) => slaClassRank(a.breachClass) - slaClassRank(b.breachClass),
  );
}

/**
 * LEAD-INBOX-SLA-AGING-AND-QUEUE-HEALTH-V1 (Refs #315): the compact per-lead SLA
 * aging chip(s) on an intake row -- the EXISTING V4-P0 breach state surfaced so
 * an operator sees "Unclaimed 14m" on the row itself (FIVE-SECOND TEST). ONE
 * chip per breached class, in the canonical engine order; danger tone once a
 * breach doubles its threshold (severity 'red'), pending otherwise -- the SAME
 * tone mapping the Lead-360 SlaCell uses. Renders NOTHING when the row has no
 * active breach (null slaState). NARROW: class token + whole waiting-age minutes
 * only -- no PII. The per-class detail + action hint live on Lead 360.
 */
export function SlaBreachChip({
  slaState,
}: {
  slaState: Lead360SlaState | null | undefined;
}): JSX.Element | null {
  const breaches = orderedBreaches(slaState);
  if (breaches.length === 0) {
    return null;
  }
  return (
    <span className="cc-row__sla" data-testid="cc-row-sla-group">
      {breaches.map((breach) => {
        const age = formatLeakageAge(breach.ageMinutes);
        const title = isKnownBreachClass(breach.breachClass)
          ? `${classMeta(breach.breachClass).title} - waiting ${age}`
          : `SLA breach - waiting ${age}`;
        return (
          <AcBadge
            key={breach.breachClass}
            tone={breach.severity === 'red' ? 'danger' : 'pending'}
            testId="cc-row-sla"
            title={title}
          >
            {`${classShortLabel(breach.breachClass)} ${age}`}
          </AcBadge>
        );
      })}
    </span>
  );
}

export function IntakeQueueRow({
  row,
  actions,
  leadDetailReachable = true,
}: {
  row: CallcenterQueueRowView;
  actions?: RowActionHandlers;
  /** NEVER-BOUNCE RULE (item 1); default true = today's behavior. */
  leadDetailReachable?: boolean;
}): JSX.Element {
  return (
    <tr className="cc-row" data-testid="cc-intake-row">
      <td className="cc-row__name">
        <LeadNameCell
          href={drillHrefFor(row.safeLeadId, leadDetailReachable)}
          label={row.patientName}
          testId="cc-row-drill"
        />
        <span className="cc-row__id"> #{row.leadId}</span>
        <ReturningChip tier={row.returningVisitorTier} />
        <SlaBreachChip slaState={row.slaState} />
      </td>
      <td className="cc-row__source">{row.sourceLabel ?? '-'}</td>
      <td className="cc-row__stage">
        <span className="status-chip" data-testid="cc-row-stage">
          {stageLabel(row.stage)}
        </span>
      </td>
      <td className="cc-row__owner">
        <OwnerCell ownership={row.ownerAssignment} />
      </td>
      <td className="cc-row__age" data-testid="cc-row-age">
        {formatAge(row.ageMinutes)}
      </td>
      <td className="cc-row__next" data-testid="cc-row-next">
        {row.nextActionLabel}
      </td>
      <td className="cc-row__action">
        <PrimaryAction
          row={row}
          actions={actions}
          leadDetailReachable={leadDetailReachable}
        />
      </td>
    </tr>
  );
}

export function PreVisitList({
  rows,
  leadDetailReachable = true,
}: {
  rows: readonly PreVisitQueueRowView[];
  /** NEVER-BOUNCE RULE (item 1); default true = today's behavior. */
  leadDetailReachable?: boolean;
}): JSX.Element {
  return (
    <section className="cc-previsit" data-testid="cc-previsit">
      <h3 className="cc-section__title">Pre-visit follow-up (soonest visit first)</h3>
      {rows.length === 0 ? (
        <p className="cc-empty" data-testid="cc-previsit-empty">
          {CC_EMPTY_PREVISIT}
        </p>
      ) : (
        <table className="cc-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.leadId} className="cc-row" data-testid="cc-previsit-row">
                <td>
                  <LeadNameCell
                    href={drillHrefFor(r.safeLeadId, leadDetailReachable)}
                    label={r.patientName}
                    testId="cc-previsit-drill"
                  />
                </td>
                <td>
                  {r.appointmentTimeIso
                    ? formatTimestampIst(r.appointmentTimeIso)
                    : '-'}
                </td>
                <td data-testid="cc-previsit-attempts">{r.attemptCount} attempts</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// --- Chase queue (V2-P3): enquired-never-booked + booked-never-visited -------

/** Attempts label: "N/M attempts". Pure. */
export function attemptsLabel(d: ChaseDisciplineView): string {
  return `${d.attemptCount}/${d.maxAttempts} attempts`;
}

/** Next-attempt label: exhausted / due now / due at instant / '-'. Pure. */
export function nextAttemptLabel(d: ChaseDisciplineView): string {
  if (d.attemptsExhausted) return 'Attempts exhausted';
  if (d.nextAttemptDueAtIso === null) return '-';
  if (d.nextAttemptDue) return 'Due now';
  return `Next attempt ${formatTimestampIst(d.nextAttemptDueAtIso)}`;
}

export interface ChaseActionHandlers {
  readonly reasons: readonly PrevisitDispositionReasonView[];
  readonly onRecord?: (leadId: number, reasonId: string) => void;
  readonly busyLeadId?: number | null;
}

/** One chase row: name, class, attempts, next-attempt-due, and the config-driven
 *  closure actions (Not Reachable gated on attempt exhaustion; Duplicate always
 *  available). WhatsApp-before-Not-Reachable is a NAMED GAP, shown honestly. */
export function ChaseRow({
  row,
  actions,
  leadDetailReachable = true,
}: {
  row: ChaseQueueRowView;
  actions?: ChaseActionHandlers;
  /** NEVER-BOUNCE RULE (item 1); default true = today's behavior. */
  leadDetailReachable?: boolean;
}): JSX.Element {
  const busy = actions?.busyLeadId === row.leadId;
  const reasons = actions?.reasons ?? [];
  return (
    <tr className="cc-row" data-testid="cc-chase-row">
      <td className="cc-row__name">
        <LeadNameCell
          href={drillHrefFor(row.safeLeadId, leadDetailReachable)}
          label={row.displayName}
          testId="cc-chase-drill"
        />
        <span className="cc-row__id"> #{row.leadId}</span>
      </td>
      <td>
        <span className="status-chip" data-testid="cc-chase-class">
          {CHASE_CLASS_LABEL[row.chaseClass]}
        </span>
      </td>
      <td data-testid="cc-chase-attempts">{attemptsLabel(row.discipline)}</td>
      <td data-testid="cc-chase-next">{nextAttemptLabel(row.discipline)}</td>
      <td className="cc-chase__actions">
        {reasons.map((reason) => {
          const gated =
            reason.requiresAttemptsExhausted &&
            !row.discipline.notReachableEligible;
          return (
            <button
              key={reason.id}
              type="button"
              className={`ac-btn ac-btn--sm ${
                reason.eventType === 'not_reachable' ? 'ac-btn--danger' : 'ac-btn--secondary'
              }`}
              data-testid={`cc-chase-reason-${reason.id}`}
              disabled={gated || busy || actions?.onRecord === undefined}
              title={
                gated
                  ? `Requires ${row.discipline.maxAttempts} contact attempts (${row.discipline.attemptCount} recorded)`
                  : undefined
              }
              onClick={
                actions?.onRecord
                  ? () => actions.onRecord?.(row.leadId, reason.id)
                  : undefined
              }
            >
              {reason.label}
            </button>
          );
        })}
        {!row.discipline.whatsappFollowupAvailable ? (
          <span className="cc-chase__wa" data-testid="cc-chase-whatsapp">
            {CC_WHATSAPP_UNAVAILABLE}
          </span>
        ) : null}
      </td>
    </tr>
  );
}

export function ChaseList({
  data,
  actions,
  leadDetailReachable = true,
}: {
  data: CallcenterQueueView;
  actions?: ChaseActionHandlers;
  /** NEVER-BOUNCE RULE (item 1); default true = today's behavior. */
  leadDetailReachable?: boolean;
}): JSX.Element {
  const rows = data.chase.rows;
  const d = data.chase.discipline;
  const reasons = actions?.reasons ?? data.previsitDispositionReasons;
  const anyProvisional = reasons.some((r) => r.provisional);
  const chaseActions: ChaseActionHandlers | undefined = actions
    ? { ...actions, reasons }
    : { reasons };
  return (
    <section className="cc-chase" data-testid="cc-chase">
      <h3 className="cc-section__title">{CC_CHASE_TITLE}</h3>
      <p className="cc-section__hint" data-testid="cc-chase-policy">
        Contact discipline: {d.maxAttempts} attempts over {d.windowDaysMin}-
        {d.windowDaysMax} days before Not Reachable.
      </p>
      {rows.length === 0 ? (
        <p className="cc-empty" data-testid="cc-chase-empty">
          {CC_EMPTY_CHASE}
        </p>
      ) : (
        <table className="cc-table">
          <tbody>
            {rows.map((r) => (
              <ChaseRow
                key={`${r.chaseClass}-${r.leadId}`}
                row={r}
                actions={chaseActions}
                leadDetailReachable={leadDetailReachable}
              />
            ))}
          </tbody>
        </table>
      )}
      {anyProvisional ? (
        <p className="cc-chase__provisional" data-testid="cc-chase-provisional">
          {CC_CHASE_PROVISIONAL}
        </p>
      ) : null}
    </section>
  );
}

/** The config-backed qualification checklist (S2). Rendered when the operator
 *  opens Qualify on a row; confirming advances the stage through the engine. */
export function QualifyPanel({
  items,
  checked,
  busy,
  message,
  onToggle,
  onConfirm,
  onCancel,
}: {
  items: readonly string[];
  checked: ReadonlySet<string>;
  busy: boolean;
  message: string | null;
  onToggle?: (item: string) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
}): JSX.Element {
  const allChecked = items.length > 0 && items.every((i) => checked.has(i));
  return (
    <div className="cc-qualify" data-testid="cc-qualify-panel">
      <p className="cc-qualify__hint">{CC_QUALIFY_HINT}</p>
      <ul className="cc-qualify__items">
        {items.map((item) => (
          <li key={item}>
            <label>
              <input
                type="checkbox"
                checked={checked.has(item)}
                readOnly={!onToggle}
                data-testid={`cc-qualify-item-${item}`}
                onChange={onToggle ? () => onToggle(item) : undefined}
              />
              {stageLabel(item)}
            </label>
          </li>
        ))}
      </ul>
      {message ? (
        <p className="cc-qualify__msg" role="alert" data-testid="cc-qualify-msg">
          {message}
        </p>
      ) : null}
      <div className="cc-qualify__actions">
        <button
          type="button"
          className="ac-btn ac-btn--primary ac-btn--sm"
          data-testid="cc-qualify-confirm"
          disabled={!allChecked || busy}
          onClick={onConfirm}
        >
          {CC_QUALIFY_CONFIRM}
        </button>
        <button type="button" className="ac-btn ac-btn--secondary ac-btn--sm" data-testid="cc-qualify-cancel" onClick={onCancel}>
          {CC_QUALIFY_CANCEL}
        </button>
      </div>
    </div>
  );
}

/** The pure command-center READY view (the 30-second-contract render target). */
/**
 * One-line role framing for the command-center identity (F6). Gives the page a
 * clear, consistent title + a role-specific subtitle so the nav label, the page
 * title, and the desk label no longer read as three different surfaces.
 */
export function commandCenterFraming(role?: DashboardMe['role']): string {
  if (role === 'call_center_agent') return 'Your shared queue.';
  if (role === 'receptionist') return 'Shared lead queue - front-office coverage.';
  return 'Shared lead queue.';
}

export function CommandCenterView({
  data,
  activeFilter,
  onFilter,
  overdueFirst = false,
  onToggleOverdueFirst,
  rowActions,
  chaseActions,
  viewerRole,
  leadDetailReachable = true,
}: {
  data: CallcenterQueueView;
  activeFilter: IntakeFilter;
  onFilter?: (f: IntakeFilter) => void;
  /** Part D: OPT-IN "SLA breaches first" sort (default false -> byte-identical order). */
  overdueFirst?: boolean;
  onToggleOverdueFirst?: () => void;
  rowActions?: RowActionHandlers;
  chaseActions?: ChaseActionHandlers;
  viewerRole?: DashboardMe['role'];
  /**
   * NEVER-BOUNCE RULE (DEEPAK-FRONT-OFFICE-FEEDBACK-CORRECTIVE-V1, item 1):
   * whether the caller can actually OPEN the canonical Lead-360 page
   * (canReachLeadDetail = me.leadsWorkspaceEnabled). FALSE -> every lead row
   * renders as non-navigating text plus the honest read-only note, so a
   * manager can never click into a silent redirect back to their own home.
   * Default TRUE keeps every existing caller and pin byte-identical; the live
   * container below ALWAYS passes the server-computed value, so no production
   * render ever relies on the default.
   */
  leadDetailReachable?: boolean;
}): JSX.Element {
  const filtered = filterIntakeRows(data.intake.rows, activeFilter);
  // Part D: the sort is OPT-IN; when off the filtered order is byte-identical
  // to the server's default (oldest-first) -- never reordered.
  const rows = overdueFirst ? sortOverdueFirst(filtered) : filtered;
  const anyBreached = data.intake.rows.some(
    (r) => (r.slaState?.breaches ?? []).length > 0,
  );
  return (
    <div className="cc" data-testid="callcenter-command-center">
      <header className="cc-head">
        <h1 className="cc-title">Command center</h1>
        <p className="cc-subtitle" data-testid="cc-role-framing">
          {commandCenterFraming(viewerRole)}
        </p>
      </header>
      <IntakeSummaryStrip data={data} activeFilter={activeFilter} onFilter={onFilter} />
      <section className="cc-intake">
        <div className="cc-intake__head">
          <h3 className="cc-section__title">
            {overdueFirst
              ? 'New enquiries (SLA breaches first)'
              : 'New enquiries (oldest first)'}
          </h3>
          {/* Part D: opt-in SLA-breaches-first sort. Rendered only when the queue
              has at least one breach (no dead control on a healthy queue) and a
              handler is wired. Default OFF -> default order unchanged. The copy
              says "SLA breaches first" (not "Overdue first") so it never
              collides with the queue's existing escalation-based "Overdue"
              metric; the internal id (sortOverdueFirst / overdueFirst /
              cc-sort-overdue-first) stays stable. */}
          {onToggleOverdueFirst && anyBreached ? (
            <button
              type="button"
              className={`cc-chip cc-chip--sort${overdueFirst ? ' cc-chip--active' : ''}`}
              aria-pressed={overdueFirst}
              data-testid="cc-sort-overdue-first"
              onClick={onToggleOverdueFirst}
            >
              SLA breaches first
            </button>
          ) : null}
        </div>
        {rows.length === 0 ? (
          <p className="cc-empty" data-testid="cc-intake-empty">
            {CC_EMPTY_INTAKE}
          </p>
        ) : (
          <table className="cc-table" data-testid="cc-intake-table">
            <tbody>
              {rows.map((r) => (
                <IntakeQueueRow
                  key={r.leadId}
                  row={r}
                  actions={rowActions}
                  leadDetailReachable={leadDetailReachable}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
      <PreVisitList
        rows={data.previsit.rows}
        leadDetailReachable={leadDetailReachable}
      />
      <ChaseList
        data={data}
        actions={chaseActions}
        leadDetailReachable={leadDetailReachable}
      />
      <InboundCallPanel
        activity={data.inboundCalls}
        leadDetailReachable={leadDetailReachable}
      />
    </div>
  );
}

// --- Phase 3: the inbound call panel ----------------------------------------

/**
 * The identity state's badge tone. Semantic only -- identity never borrows a
 * status color (design law 03 P2). A NEW CALLER is `info`, not `danger`: an
 * unrecognised number is the most valuable event this product receives, and
 * colouring it like a failure would teach the operator to treat it as one.
 */
const CALL_IDENTITY_TONE: Readonly<
  Record<InboundCallIdentityStateView, 'brand' | 'positive' | 'info' | 'pending'>
> = {
  matched_lead: 'brand',
  matched_patient: 'positive',
  unknown_caller: 'info',
  resolution_failed: 'pending',
};

/** The order the summary chips read in: known work first, then what needs a human. */
const CALL_IDENTITY_ORDER: readonly InboundCallIdentityStateView[] = [
  'matched_lead',
  'matched_patient',
  'unknown_caller',
  'resolution_failed',
];

const CALL_IDENTITY_CHIP_LABEL: Readonly<
  Record<InboundCallIdentityStateView, string>
> = {
  matched_lead: 'Known enquiry',
  matched_patient: 'Known patient',
  unknown_caller: 'New caller',
  resolution_failed: 'Not identified',
};

/**
 * What the row calls the person. A known enquiry carries its carved
 * administrative name; every other state says WHAT it is rather than showing a
 * blank or a bare id. No state renders an unlabelled value (craft bar).
 */
export function callerLabel(row: InboundCallActivityRowView): string {
  if (row.displayName !== null && row.displayName.trim().length > 0) {
    return row.displayName;
  }
  if (row.identity === 'matched_patient') return 'A patient of this clinic';
  if (row.identity === 'unknown_caller') return 'Someone new';
  if (row.identity === 'matched_lead') return 'A known enquiry';
  return 'Caller not identified';
}

function InboundCallRow({
  row,
  leadDetailReachable,
  showNextAction,
}: {
  row: InboundCallActivityRowView;
  leadDetailReachable: boolean;
  /** FALSE on the monitor variant: the next action belongs to the team that
   *  owns the call, so the column is STRUCTURALLY ABSENT here rather than
   *  hidden -- a supervisor never gets told to do the owner's next step. */
  showNextAction: boolean;
}): JSX.Element {
  // NEVER-BOUNCE RULE (item 1, reused): a caller who cannot open the Lead-360
  // page gets non-navigating text plus the honest read-only note -- never a
  // link into a silent redirect back to their own home.
  const canOpenLead = row.leadId !== null && leadDetailReachable;
  return (
    <tr className="cc-row" data-testid="cc-call-row">
      <td className="cc-row__name" data-testid="cc-call-who">
        {canOpenLead ? (
          <a className="cc-chip cc-chip--link" href={`/dashboard/leads/${row.leadId}`}>
            {callerLabel(row)}
          </a>
        ) : (
          callerLabel(row)
        )}
        {row.leadId !== null && !leadDetailReachable ? (
          <span className="cc-row__readonly">{QUICKVIEW_READONLY_COPY}</span>
        ) : null}
      </td>
      <td data-testid="cc-call-identity">
        <AcBadge tone={CALL_IDENTITY_TONE[row.identity]} testId="cc-call-identity-badge">
          {row.identityLabel}
        </AcBadge>
      </td>
      <td className="cc-row__source" data-testid="cc-call-channel">
        {row.channelLabel}
      </td>
      <td className="cc-row__stage" data-testid="cc-call-outcome">
        {row.outcomeLabel}
      </td>
      <td className="cc-row__age" data-testid="cc-call-when">
        {formatTimestampIst(row.occurredAtIso)}
      </td>
      {showNextAction ? (
        <td className="cc-row__next" data-testid="cc-call-next">
          {row.nextAction}
        </td>
      ) : null}
    </tr>
  );
}

/**
 * CALLS THAT CAME IN -- the first call-centre surface in AdoraCare that reads
 * the calls table. Before this panel a call reached an operator only
 * indirectly, as a lead, and a call from an unrecognised number reached them
 * not at all.
 *
 * Four states, four words, every one labelled. An empty panel says WHY it is
 * empty. No control is offered that cannot act: there is no outbound calling
 * carrier, so there is no call button -- only the sentence saying so.
 */
export function InboundCallPanel({
  activity,
  leadDetailReachable = true,
  variant = 'operator',
}: {
  activity: InboundCallActivityView | undefined;
  leadDetailReachable?: boolean;
  /**
   * 'operator' (default) -- the call centre's own panel: the operator OWNS
   * these calls, so each row carries its next action.
   * 'monitor' -- a supervisor's panel (the COC station): the SAME signal,
   * the SAME classifier, the SAME words, but NO next-action affordance,
   * because the next action is somebody else's. The difference is
   * structural, never CSS.
   */
  variant?: 'operator' | 'monitor';
}): JSX.Element {
  // FAIL-SOFT PRODUCT LAW (the pin in CallcenterCommandCenterSla.test.tsx):
  // CommandCenterView must render a PARTIAL view without throwing. The live
  // fetch already substitutes an empty block in normalizeCallcenterQueue, but
  // a caller holding an older or hand-built shape must not crash the whole
  // command center over an additive field -- so the panel tolerates it here
  // too, at the one place every caller passes through.
  const safe: InboundCallActivityView = activity ?? emptyInboundCallActivity();
  const counts = safe.countsByIdentity ?? {
    matched_lead: 0,
    matched_patient: 0,
    unknown_caller: 0,
    resolution_failed: 0,
  };
  const rows = Array.isArray(safe.rows) ? safe.rows : [];
  const chips = CALL_IDENTITY_ORDER.filter((state) => (counts[state] ?? 0) > 0);
  const isMonitor = variant === 'monitor';
  return (
    <section
      className="cc-calls"
      data-testid={isMonitor ? 'cc-calls-monitor' : 'cc-calls'}
    >
      <div className="cc-intake__head">
        <h3 className="cc-section__title">
          {CC_CALLS_TITLE}
          {rows.length > 0 ? ` (last ${safe.windowHours}h)` : ''}
        </h3>
        {chips.length > 0 ? (
          <div className="cc-strip__chips" data-testid="cc-calls-chips">
            {chips.map((state) => (
              <span
                key={state}
                className="cc-chip cc-chip--static"
                data-testid={`cc-calls-chip-${state}`}
              >
                {CALL_IDENTITY_CHIP_LABEL[state]} {counts[state]}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <p className="cc-section__hint">
        {isMonitor ? CC_CALLS_MONITOR_HINT : CC_CALLS_HINT}
      </p>
      {rows.length === 0 ? (
        <p className="cc-empty" data-testid="cc-calls-empty">
          {safe.emptyLabel ?? 'No calls have come in during this window.'}
        </p>
      ) : (
        <>
          <table className="cc-table" data-testid="cc-calls-table">
            <tbody>
              {rows.map((row) => (
                <InboundCallRow
                  key={row.callId}
                  row={row}
                  leadDetailReachable={leadDetailReachable}
                  showNextAction={!isMonitor}
                />
              ))}
            </tbody>
          </table>
          <p
            className="cc-chase__wa"
            data-testid={
              isMonitor ? 'cc-calls-monitor-line' : 'cc-calls-no-outbound-line'
            }
          >
            {isMonitor ? CC_CALLS_MONITOR_LINE : CC_CALLS_NO_OUTBOUND_LINE}
          </p>
        </>
      )}
    </section>
  );
}

// --- Error mapping (friendly, PHI-free; the DELTA-A qualify case is honest) --


function messageFor(err: unknown): string {
  if (err instanceof PortalApiError) {
    if (err.status === 401) {
      // The stage engine's config-consulted role-authority gate
      // (STAGE-ADVANCE-AUTHORITY-CONFIG-V1 / V2-P0d: the stage's default owner
      // UNION org-configured extra roles). For qualify, a call_center_agent now
      // SUCCEEDS when the org seeds qualified -> { receptionist,
      // call_center_agent } (Partha Q5); a 401 here means the org has NOT
      // granted this role that stage (or the role truly owns no path to it).
      // Surface honestly, never silently.
      return 'Your role is not authorized for this action.';
    }
    if (err.status === 409) return 'This lead just changed. Reload and retry.';
    if (err.message.trim().length > 0) return err.message;
  }
  if (err instanceof Error && err.message.trim().length > 0) return err.message;
  return 'Something went wrong. Retry.';
}

// --- Live container (default export; the route mounts this) ------------------

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: CallcenterQueueView }
  | { kind: 'error'; message: string };

interface QualifyState {
  readonly leadId: number;
  readonly checked: Set<string>;
  readonly busy: boolean;
  readonly message: string | null;
}

interface Toast {
  readonly tone: 'success' | 'error';
  readonly text: string;
}

export default function CallcenterCommandCenter(): JSX.Element {
  const ctx = useDashboardMeContext();
  const me: DashboardMe | null = ctx?.me ?? null;
  const claimAffordance = useMemo(() => resolveClaimAffordance(me), [me]);

  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [activeFilter, setActiveFilter] = useState<IntakeFilter>('all');
  const [busyLeadId, setBusyLeadId] = useState<number | null>(null);
  const [qualify, setQualify] = useState<QualifyState | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [filterTouched, setFilterTouched] = useState<boolean>(false);
  // Part D: opt-in "SLA breaches first" sort (default OFF -> byte-identical order).
  const [overdueFirst, setOverdueFirst] = useState<boolean>(false);

  const load = useCallback(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchCallcenterQueue()
      .then((data) => {
        if (cancelled) return;
        setState({ kind: 'ready', data });
        // Default to my-queue when ownership is available and the operator has
        // not chosen a filter yet (03: my-queue default).
        if (!filterTouched) {
          setActiveFilter(data.intake.health.ownershipAvailable ? 'mine' : 'all');
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({ kind: 'error', message: messageFor(e) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filterTouched]);

  useEffect(() => load(), [load]);

  const onFilter = useCallback((f: IntakeFilter) => {
    setFilterTouched(true);
    setActiveFilter(f);
  }, []);

  const onClaim = useCallback(
    (leadId: number) => {
      setBusyLeadId(leadId);
      setToast(null);
      claimLead(leadId)
        .then(() => {
          setToast({ tone: 'success', text: 'Lead claimed.' });
          load();
        })
        .catch((e: unknown) => setToast({ tone: 'error', text: messageFor(e) }))
        .finally(() => setBusyLeadId(null));
    },
    [load],
  );

  const onQualifyOpen = useCallback((row: CallcenterQueueRowView) => {
    setToast(null);
    setQualify({ leadId: row.leadId, checked: new Set(), busy: false, message: null });
  }, []);

  const onQualifyToggle = useCallback((item: string) => {
    setQualify((q) => {
      if (q === null) return q;
      const next = new Set(q.checked);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return { ...q, checked: next, message: null };
    });
  }, []);

  const onQualifyConfirm = useCallback(() => {
    setQualify((q) => (q === null ? q : { ...q, busy: true, message: null }));
    setQualify((current) => {
      if (current === null) return current;
      // Fire the EXISTING stage engine (no new stage logic).
      advanceLeadStage(current.leadId, 'qualified')
        .then(() => {
          setToast({ tone: 'success', text: 'Lead qualified.' });
          setQualify(null);
          load();
        })
        .catch((e: unknown) =>
          setQualify((q) =>
            q === null ? q : { ...q, busy: false, message: messageFor(e) },
          ),
        );
      return current;
    });
  }, [load]);

  const onQualifyCancel = useCallback(() => setQualify(null), []);

  const onRecordDisposition = useCallback(
    (leadId: number, reasonId: string) => {
      setBusyLeadId(leadId);
      setToast(null);
      recordPrevisitDisposition({ leadId, reasonId })
        .then((res) => {
          setToast({
            tone: 'success',
            text: res.alreadyRecorded
              ? 'Already recorded.'
              : 'Disposition recorded.',
          });
          load();
        })
        .catch((e: unknown) => setToast({ tone: 'error', text: messageFor(e) }))
        .finally(() => setBusyLeadId(null));
    },
    [load],
  );

  if (state.kind === 'loading') {
    return (
      <div className="cc cc--loading" data-testid="cc-loading" aria-busy="true">
        <div className="cc-skel cc-skel--strip" />
        <div className="cc-skel cc-skel--row" />
        <div className="cc-skel cc-skel--row" />
        <div className="cc-skel cc-skel--row" />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="cc cc--error" data-testid="cc-error" role="alert">
        <p>{state.message || CC_ERROR_FALLBACK}</p>
        <button type="button" className="ac-btn ac-btn--secondary ac-btn--sm" onClick={() => load()}>
          Retry
        </button>
      </div>
    );
  }

  const rowActions: RowActionHandlers = {
    claimAffordance,
    onClaim,
    onQualify: onQualifyOpen,
    busyLeadId,
  };

  const chaseActions: ChaseActionHandlers = {
    reasons: state.data.previsitDispositionReasons,
    onRecord: onRecordDisposition,
    busyLeadId,
  };

  return (
    <div className="cc-container">
      {toast ? (
        <p
          className={`cc-toast cc-toast--${toast.tone}`}
          role={toast.tone === 'error' ? 'alert' : 'status'}
          data-testid="cc-toast"
        >
          {toast.text}
        </p>
      ) : null}
      <CommandCenterView
        data={state.data}
        activeFilter={activeFilter}
        onFilter={onFilter}
        overdueFirst={overdueFirst}
        onToggleOverdueFirst={() => setOverdueFirst((v) => !v)}
        rowActions={rowActions}
        chaseActions={chaseActions}
        viewerRole={me?.role}
        // NEVER-BOUNCE RULE (item 1): the live surface ALWAYS passes the
        // server-computed reachability, never the prop default.
        leadDetailReachable={canReachLeadDetail(me)}
      />
      {qualify ? (
        <QualifyPanel
          items={state.data.qualificationChecklist}
          checked={qualify.checked}
          busy={qualify.busy}
          message={qualify.message}
          onToggle={onQualifyToggle}
          onConfirm={onQualifyConfirm}
          onCancel={onQualifyCancel}
        />
      ) : null}
    </div>
  );
}
